<template>
  <main-section :is-map="true">
    <div :style="{ gridTemplateRows: 'min-content auto' }" class="grid h-full w-full grid-rows-2">
      <div class="grid grid-cols-4 gap-4 p-2">
        <parking-status-card :parking-spot-count="parkingSpotCount" />
      </div>
      <map-loader v-if="isLoading" />
      <grid :column-number="2" padding="p-4">
        <div id="map" class="h-full w-full"></div>
        <grid :row-number="2">
          <div class="max-h-[350px] border">
            <custom-table
              :force-refresh="true"
              :is-action-list-not-displayed="true"
              :remove-table-header="true"
              :show-per-page="8"
              :table-data="filteredEvents"
              :table-headers="tableHeaders"
              table-height="100%"
            />
          </div>
          <div class="border">
            <apexchart
              :key="chartSeries.length"
              ref="apexChart"
              type="line"
              :options="chartOptions"
              :series="chartSeries"
              height="320"
            />
          </div>
        </grid>
      </grid>
    </div>
  </main-section>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import MainSection from "@/core/Components/MainSection.vue";
import Grid from "@/core/Components/Grid.vue";
import MapService from "@/modules/MapView/Services/MapService";
import ParkingStatusCard from "@/modules/Parkom/Components/ParkingStatusCard.vue";
import MarkerService from "@/modules/MapView/Services/MarkerService";
import DashboardService from "@/modules/Parkom/Services/DashboardService";
import {
  CompanyDefaultDevice,
  ParkingSpotConfig,
  ParkingSpotsStatus,
  WebsocketMessage,
} from "@/modules/Parkom/Services/DashboardTypes";
import { useToast } from "vue-toastification";
import { ErrorMessageFormatter, isString, FormatDateTime } from "@/core/Services/GlobalFunctions";
import { ParkingSpot, ParkingSpotMap } from "@/modules/Parkom/Submodules/ParkingSpots/Services/ParkingSpotTypes";
import ParkingSpotService from "@/modules/Parkom/Submodules/ParkingSpots/Services/ParkingSpotService";
import MapLoader from "@/modules/MapView/Components/MapLoader.vue";
import DisplayService from "@/modules/Parkom/Submodules/Displays/Services/DisplayService";
import { Display, DisplayMap } from "@/modules/Parkom/Submodules/Displays/Services/DisplayTypes";
import WebSocketService from "@/core/WebSocket/WebSocketService";
import L from "leaflet";
import CustomTable from "@/core/Components/CustomTable.vue";
import TableActionHeader from "@/core/Components/TableActionHeader.vue";
import { DxPager } from "devextreme-vue/data-grid";
import UserCustomisableGraphService from "@/modules/MapView/Services/UserCustomisableGraphService";
import { ApexOptions } from "apexcharts";
import moment from "moment";
import { SelectMapIcon } from "@/modules/Parkom/Services/DashboardHelpers";
import { useChangeCompanyStore } from "@/core/Store/changeCompanyStore";
import { useParkingSpaceFilterStore } from "@/core/Store/useParkingSpaceFilterStore";
import ConfigurationService from "@/core/Services/ConfigurationService";
import { MarkerOptionsExtended } from "node_modules/@types/leaflet/index.d.ts";
import { useDigitalParkingSigns } from "@/core/Store/useDigitalParkingSigns";

export default defineComponent({
  components: {
    DxPager,
    TableActionHeader,
    CustomTable,
    MapLoader,
    ParkingStatusCard,
    Grid,
    MainSection,
  },
  //Injecting from main.ts, so we have one instance of a dashboard events store
  //If store is initialized in this component, it will be initialized for every render of a page
  //What is needed is that events stay after route change
  inject: ["dashboardEventsStore", "initalLoad"],
  data() {
    return {
      //initialMounted: false,
      mapService: {} as MapService,
      markerService: {} as MarkerService,
      dashboardService: new DashboardService(),
      parkingSpotService: new ParkingSpotService(),
      displayService: new DisplayService(),
      graphService: new UserCustomisableGraphService(),
      parkingSpaceStatus: {} as ParkingSpotsStatus,
      parkingSpots: [] as ParkingSpotMap[],
      displaysMap: [] as DisplayMap[],
      chart: {} as ApexCharts,
      displays: [] as Display[],
      companyDefaultDevice: {} as CompanyDefaultDevice,
      eventsStore: this.dashboardEventsStore as any,
      filteredEvents: [] as any[],
      webSocketService: new WebSocketService(`${ConfigurationService.configData.WEB_SOCKET_PREFIX_PATH}/device`),
      isLoading: false,
      toast: useToast(),
      changeCompanyStore: useChangeCompanyStore(),
      parkingSpaceFilterStore: useParkingSpaceFilterStore(),
      digitalParkingSignsStore: useDigitalParkingSigns(),
      deviceUuids: [] as { deviceUuid: string }[],
      mappedParkingSpots: new Map<number, { parkingSpotName: string; parkingSpaceName: string, parkingSpaceId: number }>(),
      selectedParkingSpots: [] as number[],
      parkingSpotMarkerMap: new Map<number, L.Marker>(),
      displayMarkerMap: new Map<number, { marker: L.Marker; name: string; isOffline: boolean }>(),
      canvasWidth: 0,
      displayWidth: 0,
      tableHeaders: {
        time: { header: "Dashboard.Time", isVisible: true },
        type: { header: "Dashboard.Type", isVisible: true },
        event: { header: "Dashboard.Event", isVisible: true },
      },

      parkingSpotCount: [
        {
          id: "total",
          name: "Dashboard.Total",
          count: 0,
          color: "text-[#5eaee6]",
          bgColor: "bg-[#e4f2ff]",
          icon: ["fas", "location-dot"],
          occupationByType: [] as {
            typeId: number;
            count: number;
          }[],
        },
        {
          id: "occupied",
          name: "Dashboard.Occupied",
          count: 0,
          color: "text-[#f68d82]",
          bgColor: "bg-[#ffefef]",
          icon: ["fas", "times"],
          occupationByType: [] as {
            typeId: number;
            count: number;
          }[],
        },
        {
          id: "unoccupied",
          name: "Dashboard.Unoccupied",
          count: 0,
          color: "text-[#7ad29a]",
          bgColor: "bg-[#f2faed]",
          icon: ["fas", "check"],
          occupationByType: [] as {
            typeId: number;
            count: number;
          }[],
        },
        {
          id: "totalOffline",
          name: "Dashboard.Offline",
          count: 0,
          color: "text-[#52525b]",
          bgColor: "bg-[#d4d4d8]",
          icon: ["fac", "wifi-slash"],
          occupationByType: [] as {
            typeId: number;
            count: number;
          }[],
        },
      ] as ParkingSpotConfig[],
      arrowMappings: {
        D: "↓",
        L: "←",
        R: "→",
        U: "↑",
        DL: "↙",
        DR: "↘",
        UL: "↖",
        UR: "↗",
      } as Record<string, string>,
      chartSeries: [] as ApexAxisChartSeries,
      chartOptions: {} as ApexOptions,
    };
  },

  async created() {
    try {
      await this.loadDisplayFont();
      await this.loadDashboardData();
    } catch (error: any) {
      if (!isString(error.response.data)) {
        this.toast.error("Unexpected error occurred.");
        return;
      }
      this.toast.error(error.response.data);
      console.log(ErrorMessageFormatter("Error: " + error));
      throw error;
    } finally {
      this.isLoading = false;
    }
  },
  async mounted() {
    //this.initialMounted = true;
    this.isLoading = true;
    try {
      this.mapService = new MapService(document.getElementById("map") as HTMLElement);
      this.mapService.initMap();

      if (!(this.mapService instanceof MapService)) {
        return;
      }
      this.markerService = new MarkerService(this.mapService.map);
    } catch (error) {
      this.toast.error(ErrorMessageFormatter(error));
      throw error;
    } finally {
      this.isLoading = false;
    }
  },

  watch: {
    async "changeCompanyStore.companyId"() {
      this.isLoading = true;
      try {
        this.webSocketService.closeWebSocketConnection();
        this.webSocketService = new WebSocketService(
          `${ConfigurationService.configData.WEB_SOCKET_PREFIX_PATH}/device`
        );
        this.mappedParkingSpots.clear();
        this.selectedParkingSpots = [];
        this.parkingSpotMarkerMap.clear();
        this.displayMarkerMap.clear();
        this.resetParkingSpotTypeStatus();
        this.markerService.removeGroup("PARKOM");
        this.markerService.removeMarkers();
        this.eventsStore.clearEvents();

        await this.parkingSpaceFilterStore.getParkingSpaceTreeList();
        await this.digitalParkingSignsStore.getDigitalParkingSignsTreeList();
        await this.loadDashboardData();
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
        console.log(ErrorMessageFormatter(error));
      } finally {
        this.isLoading = false;
      }
    },

    async "parkingSpaceFilterStore.selectedParkingSpaceIds"(value: number[]) {
      //if (this.initialMounted) { this.initialMounted = false; return; }
      this.isLoading = true;
      try {
        this.webSocketService.closeWebSocketConnection();
        this.webSocketService = new WebSocketService(
          `${ConfigurationService.configData.WEB_SOCKET_PREFIX_PATH}/device`
        );
        this.selectedParkingSpots = [];
        this.parkingSpotMarkerMap.clear();
        this.displayMarkerMap.clear();
        this.markerService.removeGroup("PARKOM");
        this.markerService.removeMarkers();
        this.resetParkingSpotTypeStatus();

        await this.loadDashboardData();
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
        console.log(ErrorMessageFormatter(error));
      } finally {
        this.isLoading = false;
      }
    },

    async "digitalParkingSignsStore.selectedDigitalParkingSignsIds"(value: number[]) {
      this.markerService.removeDisplays();
      this.createDisplayMarkers();
      this.markerService.scaleDisplayMarkers(this.mapService.map as L.Map);
      this.markerService.fitGroupToMap("PARKOM");
    },
  },

  async unmounted() {
    this.webSocketService.closeWebSocketConnection();
  },
  methods: {
    async loadDashboardData() {
      if(this.parkingSpaceFilterStore.selectedParkingSpaceIds.length == 0 && this.initalLoad){
        this.initalLoad = false;
      }
      else {
        this.initalLoad = false;
        this.updateDisplayedEvents();
        try {
          await Promise.all([this.getCompanyDefaultDevice(), this.getParkingSpaceStatus()]);

          this.webSocketService.subscribeToDevice(
            [{ deviceUuid: this.companyDefaultDevice.deviceUuid }],
            true,
            (data: any) => this.getDataFromWebSocket(data)
          );

          await this.getParkingSpotsForMap();
        } catch (error: any) {
          if (!isString(error.response.data)) {
            this.toast.error(ErrorMessageFormatter(error));
            return;
          }
          this.toast.error(error.response.data);
          console.log(ErrorMessageFormatter(error));
        }
      }
    },
    async getParkingSpaceStatus() {
      try {
        this.parkingSpaceStatus = await this.dashboardService.getParkingSpotsStatus(
          this.parkingSpaceFilterStore.selectedParkingSpaceIds
        );

        this.createGraph();

        //Map values from API into parkingSpotCount array
        this.mapParkingSpotCardInformation();

        this.createDisplayMarkers();
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
        console.log(ErrorMessageFormatter(error));
      }
    },

    async bindToolTipToMarker(e: L.LeafletEvent) {
      const id = e.target.options.id;
      const bleTag = e.target.options.bleTag;

      e.target
        .setPopupContent(
          `<div class="w-full flex justify-center"><div class="w-4 h-4 small-loader ease-linear rounded-full border-2 border-t-2 border-b-primary border-gray-200"></div></div>`
        )
        .openPopup();
      try {
        const parkingSpotData = await this.parkingSpotService.getParkingSpotByIdentifier(id);
        e.target.setPopupContent(this.generatePopupHtml(parkingSpotData, bleTag));
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
        console.log(ErrorMessageFormatter(error));
      }
    },

    generatePopupHtml(parkingSpot: ParkingSpot, bleTag: string) {
      const parkingSpace = (this as any).$t("Dashboard.ParkingSpace");
      const parkingSpotText = (this as any).$t("Dashboard.ParkingSpot");
      const type = (this as any).$t("Dashboard.Type");
      const unoccupiedSince = (this as any).$t("Dashboard.UnoccupiedSince");
      const occupiedSince = (this as any).$t("Dashboard.OccupiedSince");
      const lastSync = (this as any).$t("Dashboard.LastSync");
      const bleTagName = (this as any).$t("Dashboard.BleTagName");

      const occupiedText = parkingSpot.occupied ? `${occupiedSince}: ` : `${unoccupiedSince}: `;
      return `
      <div class="space-y-1">
        <p>${parkingSpace}: <strong>${parkingSpot.space}</strong></p>
        <p>${parkingSpotText}: <strong>${parkingSpot.name}</strong></p>
        <p>${type}: <strong>${parkingSpot.type}</strong> | <strong>${
        parkingSpot.occupied ? "Occupied" : "Unoccupied"
      }</strong></p>
        <p>MAC: <strong>${parkingSpot.macAddress}</strong></p>
        <p>${occupiedText}<strong>${FormatDateTime(parkingSpot.occupiedTimestamp)}</strong></p>
        <p>${lastSync}: <strong>${FormatDateTime(parkingSpot.lastSync)}</strong></p>
        ${bleTag ? `<p>${bleTagName}: <strong>${bleTag}</strong></p>` : ""}
      </div>`;
    },

    async getParkingSpotsForMap() {
      const self = this;
      try {
        this.parkingSpots = await this.parkingSpotService.getParkingSpotsForMap(
          this.parkingSpaceFilterStore.selectedParkingSpaceIds
        );
        this.parkingSpots.forEach((parkingSpot) => {
          const marker = this.markerService.addMarker({
            lat: parkingSpot.latitude,
            lng: parkingSpot.longitude,
            groupKey: "PARKOM",
            icon: {
              iconUrl: `${process.env.VUE_APP_PUBLIC_PATH}map-icons/${SelectMapIcon({
                occupied: parkingSpot.occupied,
                offline: parkingSpot.offline,
                typeId: parkingSpot.typeId,
                bleTagName: parkingSpot.bleTagName,
              })}`,
              iconSize: [10, 10],
              iconAnchor: [5, 5],
            },
            markerOptions: { id: parkingSpot.id, bleTag: parkingSpot.bleTagName },
            bindPopup: true,
            returnMarker: true,
            popupOffset: { x: 0, y: 5 },
            eventHandlers: {
              click(e: L.LeafletEvent) {
                self.bindToolTipToMarker(e);
              },
            },
          }) as L.Marker;
          if (!parkingSpot.id) {
            return;
          }
          this.parkingSpotMarkerMap.set(parkingSpot.id, marker);
          this.selectedParkingSpots.push(parkingSpot.id);
        });

        const allSpots = await this.parkingSpotService.getParkingSpots({
          pageNumber: 1,
          pageSize: 100000,
        });
        allSpots.forEach(spot => {
          this.mappedParkingSpots.set(spot.id, {
              parkingSpotName: spot.name,
              parkingSpaceName: spot.space,
              parkingSpaceId: spot.spaceId,
            });
        });

        this.markerService.fitGroupToMap("PARKOM");
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
        console.log(ErrorMessageFormatter(error));
      }
    },

    getDataFromWebSocket(data: any) {
      const parsedData = JSON.parse(data.data);
      if (!parsedData.deviceUuid) {
        return;
      }

      if (parsedData.payload.type === "SpotOccupationStatus") {
        const parkingDetails = this.mappedParkingSpots.get(parsedData.payload.data.spotId);
        const tempEvent = {
          time: moment().format("YYYY-MM-DD HH:mm:ss"),
          type: parsedData.payload.data.isOccupied ? "Occupied" : "Unoccupied",
          event: `[${parkingDetails?.parkingSpotName}] - ${parkingDetails?.parkingSpaceName}`,
          spaceId: parkingDetails?.parkingSpaceId,
        };
        this.updateEventStore(tempEvent);

        if (this.selectedParkingSpots.includes(parsedData.payload.data.spotId) == false) return;

        const [total, occupied, unoccupied] = this.parkingSpotCount;
        this.updateParkingSpotStatusCardAndGraph({
          occupied,
          unoccupied,
          parsedData,
        });

        const marker = this.parkingSpotMarkerMap.get(parsedData.payload.data.spotId);

        if (!marker) {
          return;
        }

        (marker.options as MarkerOptionsExtended).bleTag = undefined;

        marker.setIcon(
          L.icon({
            iconUrl: `${process.env.VUE_APP_PUBLIC_PATH}map-icons/${SelectMapIcon({
              occupied: parsedData.payload.data.isOccupied,
              typeId: parsedData.payload.data.spotTypeId,
            })}`,
            iconSize: [10, 10],
            iconAnchor: [5, 5],
          })
        );
      } else if (parsedData.payload.type === "DisplayStatus") {
        const displayDetails: { displayId: number; value: string } = parsedData.payload.data;
        this.updateDisplayMarker(displayDetails);
      } else if (parsedData.payload.type === "SpotBleTagStatus") {
        const parkingDetails = this.mappedParkingSpots.get(parsedData.payload.data.spotId);
        const tempEvent = {
          time: moment().format("YYYY-MM-DD HH:mm:ss"),
          type: "Ble Tag",
          event: `[${parkingDetails?.parkingSpotName}] - ${parkingDetails?.parkingSpaceName} (${parsedData.payload.data.bleTagName})`,
          spaceId: parkingDetails?.parkingSpaceId,
        };
        this.updateEventStore(tempEvent);

        if (this.selectedParkingSpots.includes(parsedData.payload.data.spotId) == false) return;

        const marker = this.parkingSpotMarkerMap.get(parsedData.payload.data.spotId);

        if (!marker) {
          return;
        }

        (marker.options as MarkerOptionsExtended).bleTag = parsedData.payload.data.bleTagName;

        marker.setIcon(
          L.icon({
            iconUrl: `${process.env.VUE_APP_PUBLIC_PATH}map-icons/${SelectMapIcon({
              occupied: true,
              typeId: parsedData.payload.data.spotTypeId,
              bleTagName: parsedData.payload.data.bleTagName,
            })}`,
            iconSize: [10, 10],
            iconAnchor: [5, 5],
          })
        );
      }
    },
    updateEventStore(tempEvent: any){
      if (this.eventsStore.events.length >= 100) this.eventsStore.removeEvent();
      this.eventsStore.addEvent(tempEvent);

      this.updateDisplayedEvents();
    },
    updateDisplayedEvents() {
      this.filteredEvents = this.eventsStore.events.filter((event: any) =>
        this.parkingSpaceFilterStore.selectedParkingSpaceIds.includes(event.spaceId)
      );
    },
    updateDisplayMarker(displayDetails: { displayId: any; value: any }) {
      const displayMap = this.displayMarkerMap.get(displayDetails.displayId);
      if (!displayMap) {
        return;
      }

      displayMap.marker.setIcon(
        L.icon({
          iconUrl:
            this.createDisplayMarker({
              text: displayDetails.value,
              displayName: displayMap.name,
              isOffline: displayMap.isOffline,
            }) || "",
          iconSize: [this.displayWidth, 50],
          iconAnchor: [this.displayWidth / 2, 50],
        })
      );
      this.markerService.scaleDisplayMarker(displayMap.marker as L.Marker, this.mapService.map.getZoom());

      const display: DisplayMap = {
        id: displayDetails.displayId,
        name: displayMap.name,
        latitude: 0,
        longitude: 0,
        lastValue: displayDetails.value,
        offline: displayMap.isOffline,
      };
      displayMap.marker.setPopupContent(this.createDisplayPopupHtml(display));
    },

    async updateParkingSpotStatusCardAndGraph({
      occupied,
      unoccupied,
      parsedData,
    }: {
      occupied: ParkingSpotConfig;
      unoccupied: ParkingSpotConfig;
      parsedData: WebsocketMessage;
    }) {
      if (parsedData.payload.data.isOccupied) {
        occupied.count++;
        unoccupied.count > 0 && unoccupied.count--;
        occupied.occupationByType.find((item) => item.typeId === parsedData.payload.data.spotTypeId)!.count++;

        var founded = unoccupied.occupationByType.find((item) => item.typeId === parsedData.payload.data.spotTypeId);
        founded!.count > 0 && founded!.count--;

        let typeCount = unoccupied.occupationByType.find(
          (item) => item.typeId === parsedData.payload.data.spotTypeId
        )!.count;
        typeCount > 0 && typeCount--;
        this.updateGraph("Enters");
      } else {
        occupied.count > 0 && occupied.count--;
        unoccupied.count++;
        let typeCount = occupied.occupationByType.find(
          (item) => item.typeId === parsedData.payload.data.spotTypeId
        )!.count;
        typeCount > 0 && typeCount--;

        var founded = occupied.occupationByType.find((item) => item.typeId === parsedData.payload.data.spotTypeId);

        founded!.count > 0 && founded!.count--;

        unoccupied.occupationByType.find((item) => item.typeId === parsedData.payload.data.spotTypeId)!.count++;
        this.updateGraph("Exits");
      }
    },

    async getDisplaysForMap() {
      try {
        this.displays = await this.displayService.getDisplays({
          pageSize: 100000,
          pageNumber: 1,
        });
      } catch (error) {
        console.log(error);
        this.toast.error(ErrorMessageFormatter(error));
      }
    },
    async getCompanyDefaultDevice() {
      try {
        this.companyDefaultDevice = await this.dashboardService.getCompanyDefaultDevice();
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
        console.log(ErrorMessageFormatter(error));
      }
    },

    createDisplayMarkers() {
      this.displaysMap = this.parkingSpaceStatus.displaysMap;
      if (this.displaysMap) {
        this.displaysMap.forEach((display) => {
          if (!display.latitude && !display.longitude) {
            throw new Error("Display has no coordinates!");
          }
  
          if(!this.digitalParkingSignsStore.selectedDigitalParkingSignsIds.includes(display.id)) {
            return;
          }
  
          const marker = this.markerService.addMarker({
            lat: display.latitude,
            lng: display.longitude,
            groupKey: "PARKOM",
            bindPopup: true,
            popupOffset: { x: 0, y: -45 },
            icon: {
              iconUrl:
                this.createDisplayMarker({
                  text: display.lastValue,
                  displayName: display.name,
                  isOffline: display.offline,
                }) || "",
              iconSize: [display.lastValue ? this.displayWidth : 75, 50],
              iconAnchor: [this.displayWidth / 2, 50],
            },
            markerOptions: { id: display.name, title: display.name },
            returnMarker: true,
          });
  
          if (!marker) {
            throw new Error("Marker is undefined!");
          }
  
          marker.setPopupContent(this.createDisplayPopupHtml(display));
  
          if (!display?.id) {
            return;
          }
  
          this.displayMarkerMap.set(display.id, {
            marker: marker,
            name: display.name,
            isOffline: display.offline,
          });
        });
      }
    },

    createDisplayPopupHtml(display: DisplayMap) {
      const name = (this as any).$t("Dashboard.Name");
      const status = (this as any).$t("Dashboard.Status");
      const lastvalue = (this as any).$t("Dashboard.LastValue");
      return `
      <div class="w-[150px]">
        <div><strong>${name}: </strong><span>${display.name}</span></div>
        <div><strong>${status}: </strong><span class=${
        display.offline ? "text-error font-bold" : "text-success font-bold"
      }>${display.offline ? "Offline" : "Online"}</span></div>
        <div><strong>${lastvalue}: </strong><span>${display.lastValue}</span></div>
      </div>`;
    },
    mapParkingSpotCardInformation() {
      Object.entries(this.parkingSpaceStatus).forEach(([key, value], idx) => {
        if (key === "occupationByType" && Array.isArray(value)) {
          value.forEach((item: any) => {
            this.parkingSpotCount
              .find((item) => item.id === "occupied")!
              .occupationByType.push({
                typeId: item.typeId,
                count: item.occupied ?? 0,
              });

            this.parkingSpotCount
              .find((item) => item.id === "unoccupied")!
              .occupationByType.push({
                typeId: item.typeId,
                count: item.unoccupied ?? 0,
              });

            this.parkingSpotCount
              .find((item) => item.id === "total")!
              .occupationByType.push({
                typeId: item.typeId,
                //@ts-ignore
                count: item?.unoccupied + item?.occupied ?? 0,
              });
          });
        }
        if (key === "occupationByTypeOffline" && Array.isArray(value)) {
          const offlineConfig = this.parkingSpotCount.find((offlineItem) => offlineItem.id === "totalOffline");
          value.forEach((item: any) => {
            offlineConfig!.occupationByType.push({
              typeId: item.typeId,
              //@ts-ignore
              count: item?.unoccupied + item?.occupied ?? 0,
            });
          });
        }

        if (Array.isArray(value)) {
          return;
        }
        this.parkingSpotCount.find((item) => item.id === key)!.count = value;
      });
    },

    getMaxY(source: "occupationByHour" | "chartSeries"): number {
      let maxY = 0;
      if (source === "occupationByHour") {
        this.parkingSpaceStatus.occupationByHour.forEach((hour) => {
          maxY = Math.max(maxY, hour.enters + hour.exits);
        });
      } else {
        const entersData = this.chartSeries.find((s) => s.name === "Enters")?.data as { x: string; y: number }[];
        const exitsData = this.chartSeries.find((s) => s.name === "Exits")?.data as { x: string; y: number }[];
        entersData.forEach((enters, index) => {
          maxY = Math.max(maxY, enters.y + exitsData[index].y);
        });
      }
      return maxY;
    },

    createGraph() {
      const maxY = this.getMaxY("occupationByHour");
      const { series, options } = this.graphService.generateGraphSeriesOptions(
        {
          yPropertyConfig: [
            {
              id: "enters",
              visible: true,
              opposite: false,
              title: `${(this as any).$t("Dashboard.Enters")} - ${(this as any).$t("Dashboard.Exits")}`,
              min: 0,
              max: maxY,
            },
            {
              id: "exits",
              visible: false,
              opposite: false,
              title: `${(this as any).$t("Dashboard.Enters")} - ${(this as any).$t("Dashboard.Exits")}`,
              min: 0,
              max: maxY,
            },
            {
              id: "load",
              visible: true,
              opposite: true,
              title: (this as any).$t("Dashboard.Load") + " %",
              min: 0,
              max: 100,
              unit: "%",
            },
          ],
          xPropertyName: "hour",
          graphType: "line",
          seriesNames: [
            (this as any).$t("Dashboard.Enters"),
            (this as any).$t("Dashboard.Exits"),
            (this as any).$t("Dashboard.Load"),
          ],
          graphTitle: (this as any).$t("Dashboard.OccupationByHour"),
          types: ["bar", "bar", "line"],
          colors: ["#d4515c", "#308e1f", "#00ced1"],
        },
        this.parkingSpaceStatus.occupationByHour
      );
      this.chartSeries = series;
      this.chartOptions = options;

      this.chart = this.$refs.apexChart as ApexCharts;
    },

    updateGraph(seriesName: string) {
      const hours = new Date().getHours();
      const series = this.chartSeries.find((s) => s.name === seriesName);
      if (!series) {
        return;
      }

      const data = series.data as { x: string; y: number }[];

      const dataToUpdate = data.at(-1)!.x === moment().hour(hours).minute(0).format("YYYY-MM-DD HH:mm");

      if (dataToUpdate) {
        data.at(-1)!.y++;
      } else {
        data.push({
          x: moment().hour(hours).minute(0).format("YYYY-MM-DD HH:mm"),
          y: data.at(-1)!.y++,
        });
      }

      if (this.chartOptions && Array.isArray(this.chartOptions.yaxis) && this.chartOptions.yaxis.length == 3) {
        const maxY = this.getMaxY("chartSeries");
        this.chartOptions = {
          ...this.chartOptions,
          yaxis: [
            {
              ...this.chartOptions.yaxis[0],
              max: maxY,
            },
            {
              ...this.chartOptions.yaxis[1],
              max: maxY,
            },
            {
              ...this.chartOptions.yaxis[2],
            },
          ],
        };
      }
    },
    createDisplayMarker({ text, displayName, isOffline }: { text: string; displayName?: string; isOffline?: boolean }) {
      const ratio = window.devicePixelRatio;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const fontSize = 24;

      if (!ctx) {
        return;
      }

      ctx.font = `${fontSize}px 'My Custom Font', sans-serif`;

      const splitText = text.split(" ").map((character) => {
        if (character in this.arrowMappings) {
          character = this.arrowMappings[character];
        }

        return character;
      });

      this.canvasWidth = splitText.reduce((acc, curr) => {
        return acc + ctx.measureText(curr).width;
      }, 0);

      const charWidths = splitText.map((char) => ctx.measureText(char).width);

      const width = Math.round(this.canvasWidth) + 80;
      const height = 60;

      canvas.width = width * ratio;
      this.displayWidth = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";

      ctx.scale(ratio, ratio);

      if (!ctx) {
        return "";
      }

      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, width, height);

      ctx.fillStyle = "#333331";
      ctx.fillRect(5, 5, width - 10, height - 10);

      ctx.fillStyle = "lime";

      ctx.font = `${fontSize}px 'My Custom Font', sans-serif`;

      ctx.textBaseline = "middle";
      let spacing = Math.round((ctx.canvas.width - this.canvasWidth) / splitText.length - 1);

      let x = 10;

      splitText.forEach((text, idx) => {
        const letter = this.loadConfig(text, ctx);

        ctx.fillText(letter, x, height / 2);

        x += charWidths[idx] + spacing;
        ctx.fillStyle = "lime";
      });

      return canvas.toDataURL();
    },

    loadConfig(textSymbol: string, ctx: CanvasRenderingContext2D) {
      if (textSymbol === "X") {
        ctx.fillStyle = "red";
        return "X";
      }

      return textSymbol;
    },

    async loadDisplayFont() {
      const customFont = new FontFace("My Custom Font", `url(${process.env.VUE_APP_PUBLIC_PATH}digital-7.regular.ttf)`);

      const font = await customFont.load();

      document.fonts.add(font);
    },

    resetParkingSpotTypeStatus() {
      this.parkingSpotCount.forEach((item) => {
        item.occupationByType = [];
      });
    },
  },
});
</script>

<style>
.leaflet-popup-content p {
  margin: 0;
}
</style>
